import { motion } from "framer-motion";
import {
  Sparkles,
  CalendarHeart,
  Headphones,
  BookOpen,
  HeartPulse,
  NotebookPen,
  Users,
  MessageCircleHeart,
} from "lucide-react";

const features = [
  { icon: Sparkles, label: "AI self-coaching", body: "Personalised guidance for the moments you feel stuck. A gentle, intelligent voice meeting you where you are." },
  { icon: CalendarHeart, label: "Daily affirmations", body: "A short, hand-written affirmation that opens each day. Something to carry into the studio, or into the rest of life." },
  { icon: BookOpen, label: "Seven creative courses", body: "Programs to walk you back to your work. The 5-Minute Reset, The Becoming, sound bath libraries and more." },
  { icon: Headphones, label: "Meditations & sound", body: "Two libraries of meditations and sound healing for resets, openings, evenings, and the days you can't." },
  { icon: NotebookPen, label: "Mood journaling", body: "A quiet daily check-in. Track what's moving through you and notice the patterns underneath your work." },
  { icon: HeartPulse, label: "Instant relief videos", body: "Art-therapy tool videos for moments of strong emotion. Press play, follow along, and let your hands move you back to calm." },
  { icon: Users, label: "A small community", body: "Connect with other artists on their own wellness path. Quiet, generous, intentionally small." },
  { icon: MessageCircleHeart, label: "Coaching sessions", body: "Book a free creative alignment consultation straight from the app. Doors into the deeper work." },
];

export default function Features() {
  return (
    <section
      id="features"
      data-testid="wynn-features"
      className="relative border-t border-[#F4F1EC]/10 bg-[#0A0910] scroll-mt-20"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-28">
        <div className="grid grid-cols-12 gap-8 items-end mb-14">
          <div className="col-span-12 md:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
              Eight quiet doorways
            </div>
            <h2 className="mt-4 font-display tracking-tight leading-[1.02] text-[clamp(2rem,4.6vw,3.6rem)] font-medium text-[#F4F1EC]">
              Everything you need to walk back to{" "}
              <span className="italic font-light text-aurora">the work</span>.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.label}
                data-testid={`wynn-feature-${i + 1}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-12 sm:col-span-6 lg:col-span-3 border border-[#F4F1EC]/12 bg-[#F4F1EC]/[0.025] p-7 hover:border-[#FFB7C6]/40 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-[20px] leading-none tracking-tight text-[#F4F1EC]/40">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <Icon
                    size={20}
                    strokeWidth={1.4}
                    className="text-[#FFB7C6] group-hover:text-[#A8E6FF] transition-colors"
                  />
                </div>
                <h3 className="mt-8 font-display text-[22px] leading-tight text-[#F4F1EC]">
                  {f.label}
                </h3>
                <p className="mt-3 font-body text-[14px] leading-[1.7] text-[#F4F1EC]/65">
                  {f.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
